import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import Skeleton from "@/ui/atoms/Skeleton";
import { useState, useEffect } from "react";

export type GridLoaderProps = BoxProps & {
    rowCount?: number;
    rowHeight?: number;
};

const getColumnsForWidth = (width: number): number => {
    if (width < 768) {
        return 3;
    } else if (width < 1024) {
        return 4;
    } else if (width < 1440) {
        return 5;
    } else {
        return 6;
    }
};

const testId = "GridLoader";
const GridLoader = (props: GridLoaderProps) => {
    const { sx, rowCount = 5, rowHeight = 47, ...otherProps } = props;
    const [columns, setColumns] = useState(
        getColumnsForWidth(window.innerWidth),
    );

    useEffect(() => {
        const handleResize = () => {
            setColumns(getColumnsForWidth(window.innerWidth));
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const headerArray = [...(Array(columns) as unknown[])];
    const rowArray = [...(Array(rowCount) as unknown[])];

    return (
        <Box
            data-testid={testId}
            className={testId}
            sx={{
                width: "100%",
                height: "100%",
                backgroundColor: "var(--proda-white)",
                ...sx,
            }}
            {...otherProps}
        >
            <Box
                sx={{
                    display: "flex",
                    backgroundColor: "var(--primary-color)",
                }}
            >
                {headerArray.map((_, i) => (
                    <Box
                        key={`header-${i}`}
                        sx={{
                            flexGrow: 1,
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            padding: "1rem 0.5rem",
                            borderLeft:
                                i === 0
                                    ? "1px solid var(--proda-white-130)"
                                    : "none",
                            borderRight: "1px solid var(--proda-white-130)",
                            borderBottom: "1px solid var(--proda-white-130)",
                            height: `${rowHeight}px`,
                        }}
                    >
                        <Skeleton
                            variant="rectangular"
                            sx={{ width: "40%", height: "1.115rem" }}
                        />
                    </Box>
                ))}
            </Box>

            {rowArray.map((_, i) => (
                <Box
                    key={`row-${i}`}
                    sx={{
                        display: "flex",
                    }}
                >
                    {headerArray.map((_, j) => (
                        <Box
                            key={`cell-${i}-${j}`}
                            sx={{
                                flexGrow: 1,
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                padding: "1rem 0.5rem",
                                borderLeft:
                                    j === 0
                                        ? "1px solid var(--proda-white-130)"
                                        : "none",
                                borderRight: "1px solid var(--proda-white-130)",
                                borderBottom:
                                    "1px solid var(--proda-white-130)",
                                height: `${rowHeight}px`,
                            }}
                        >
                            <Skeleton
                                variant="rectangular"
                                sx={{ width: "70%", height: "1.115rem" }}
                            />
                        </Box>
                    ))}
                </Box>
            ))}
        </Box>
    );
};

export default GridLoader;
